/**
 * FridgeIQ — S3 Service
 * Presigned URLs for video playback + product image uploads.
 */

import { S3Client, GetObjectCommand, PutObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

const s3 = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId:     process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
});

const BUCKET = process.env.REACT_APP_S3_BUCKET;

// ── Video presigned URLs ──────────────────────────────────────────────────────

export async function getVideoUrl(s3Key, expiresIn = 3600) {
  if (!s3Key) return null;
  try {
    const command = new GetObjectCommand({ Bucket: BUCKET, Key: s3Key });
    return await getSignedUrl(s3, command, { expiresIn });
  } catch (err) {
    console.error("getVideoUrl error:", err);
    return null;
  }
}

export async function getSessionVideoUrls(session) {
  const [cam0Url, cam1Url] = await Promise.all([
    getVideoUrl(session.s3_key_cam0),
    getVideoUrl(session.s3_key_cam1),
  ]);
  return { cam0Url, cam1Url };
}

// ── Product image upload ──────────────────────────────────────────────────────

/**
 * Upload a product image to S3.
 * Stores at: product-images/{sku}.{ext}
 * Returns the S3 key on success, null on failure.
 */
export async function uploadProductImage(sku, file) {
  const ext     = file.type.split("/")[1] || "jpg";
  const s3Key   = `product-images/${sku}.${ext}`;

  try {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array  = new Uint8Array(arrayBuffer);

    await s3.send(new PutObjectCommand({
      Bucket:      BUCKET,
      Key:         s3Key,
      Body:        uint8Array,
      ContentType: file.type,
    }));

    return s3Key;
  } catch (err) {
    console.error("uploadProductImage error:", err);
    return null;
  }
}

/**
 * Get a presigned URL for a product image.
 * Cached for 24 hours since product images change rarely.
 */
export async function getProductImageUrl(s3Key, expiresIn = 86400) {
  if (!s3Key) return null;
  try {
    const command = new GetObjectCommand({ Bucket: BUCKET, Key: s3Key });
    return await getSignedUrl(s3, command, { expiresIn });
  } catch (err) {
    console.error("getProductImageUrl error:", err);
    return null;
  }
}
